<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>
    <modal-info-pedido />
    <modal-selected-embalagens />
    <modal-buscar-representante />
    <modal-buscar-cliente />
    <modal-buscar-tabela-preco />
    <modal-buscar-cond-pagamento />
    <modal-info-produto />
    <modal-info-produto-new />
    <modal-pic-pedido ref="modalPicPedido" />
    <portal-target name="modal-portal" />
    <birthdays-modal />
    <ai-chat-widget />
  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig';
import { provideToast } from 'vue-toastification/composition';
import { watch } from '@vue/composition-api';
import useAppConfig from '@core/app-config/useAppConfig';
import ModalInfoPedido from '@/components/modal/ModalInfoPedido.vue';
import ModalBuscarRepresentante from '@/components/modal/ModalBuscarRepresentante.vue';
import ModalBuscarCliente from '@/components/modal/ModalBuscarCliente.vue';
import ModalBuscarTabelaPreco from '@/components/modal/ModalBuscarTabelaPreco.vue';
import ModalBuscarCondPagamento from '@/components/modal/ModalBuscarCondPagamento.vue';
import ModalInfoProduto from '@/components/modal/ModalInfoProduto.vue';
import ModalInfoProdutoNew from '@/components/modal/ModalInfoProdutoNew.vue';
import ModalPicPedido from '@/components/modal/modal_pic_pedido.vue';
import InfoPedidoService from '@/services/InfoPedido.service';
import prioridadePedidoService from '@/services/SistemaDKP/SLSEsPriori.service';
import SLSMarcaService from '@/services/SistemaDKP/sls-marca.service';
import { useWindowSize, useCssVar } from '@vueuse/core';

import store from '@/store';
import { applyTenantColor, applyTenantSecondaryColor, applyTenantColorFromCache } from '@/utils/tenantTheme';
import { isPerfilClienteAtivo } from '@/libs/perfilCliente';
import ModalSelectedEmbalagens from '@/components/modal/ModalEmbalagens.vue';
import Swal from 'sweetalert2';
import { PortalTarget } from 'portal-vue';
import BirthdaysModal from '@/components/BirthdaysModal.vue';
import AiChatWidget from '@/components/AiChatWidget.vue';
import ToastificationContent from './@core/components/toastification/ToastificationContent.vue';

const infoPedidoService = new InfoPedidoService();
const prioridadePedido = new prioridadePedidoService();
const slsMarcaService = new SLSMarcaService();

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue');
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue');
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue');

export default {
  components: {
    ModalSelectedEmbalagens,
    ModalInfoPedido,
    ModalBuscarRepresentante,
    ModalBuscarCliente,
    ModalBuscarTabelaPreco,
    ModalBuscarCondPagamento,
    ModalInfoProduto,
    ModalInfoProdutoNew,
    ModalPicPedido,
    PortalTarget,
    BirthdaysModal,
    AiChatWidget,
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full';
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
    corPrimariaTenant() {
      return this.$store.getters['tenantStore/corPrimaria'];
    },
    corSecundariaTenant() {
      return this.$store.getters['tenantStore/corSecundaria'];
    },
  },
  async beforeCreate() {
    // Aplica cor do tenant salva no cache antes de qualquer render (evita flash)
    applyTenantColorFromCache();

    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark'];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2));
    }
    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
  },
  watch: {
    mostrarModalInfoPedido(val) {
      if (!val) {
        this.$store.dispatch('InfoPedido/prepararModalEmbalagens');
      }
    },
    corPrimariaTenant: {
      immediate: true,
      handler(newColor) {
        if (newColor) applyTenantColor(newColor);
      },
    },
    corSecundariaTenant: {
      immediate: true,
      handler(newColor) {
        if (newColor) applyTenantSecondaryColor(newColor);
      },
    },
  },
  methods: {
    async abrirModalProduto(produtoId) {
      const preenchido = this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO'];

      if (preenchido) {
        await this.$store.dispatch('InfoPedido/prepararModalEmbalagens');
        this.abrirModalEmbalagens(produtoId);
      } else {
        this.abrirModalInfoPedido();
      }
    },
    abrirModalInfoPedido() {
      this.mostrarModalInfoPedido = true;
    },
    abrirModalEmbalagens(produtoId) {
      // Verifica se dados estão prontos para abrir rapidamente o modal
      if (this.$store.state.InfoPedido.itemSelected === produtoId) {
        this.mostrarModalEmbalagens = true;
      }
    },
    async onClickItem() {
      if (!this.$store.getters['auth/IS_LOGGED_IN']) {
        await Swal.fire({
          icon: 'info',
          title: 'Usuário não autenticado',
          text: 'Por favor, faça login para continuar.',
          confirmButtonText: 'Login',
        });
        await this.$router.push({ name: 'login' });
        return;
      }

      try {
        Swal.fire({
          title: 'Carregando...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        const loadingTimer = setTimeout(() => {
          try {
            if (Swal.isVisible()) Swal.close();
          } catch (e) {
            // noop
          }
        }, 15000);

        try {
          // O perfil de cliente final pode se chamar 'Cliente' OU 'ClienteFinal' (varia por tenant).
          const isClienteFinal = isPerfilClienteAtivo();
          const firstClickFlag = localStorage.getItem('firstClickClienteFinal') === 'true';
          // Só força o modal no 1º clique se o Info Pedido ainda NÃO está
          // completo — se já está preenchido, abre o produto direto.
          if (isClienteFinal && firstClickFlag) {
            localStorage.setItem('firstClickClienteFinal', 'false');
            if (!this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO']) {
              try { if (Swal.isVisible()) Swal.close(); } catch (e) {}
              this.$bvModal.show('modal-center-info-pedido');
              return;
            }
          }
        } catch (e) {
          console.error('Erro ao verificar perfil do usuário:', e);
        }

        let validation = this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO'];

        if (!validation) {
          const userId = this.getUserId();
          const savedFormData = localStorage.getItem(`infoPedidoForm_${userId}`);

          if (savedFormData) {
            try {
              const parsedData = JSON.parse(savedFormData);

              this.$store.commit('InfoPedido/SET_FORM_INFO_PEDIDO', parsedData);

              this.$store.commit('InfoPedido/SET_CLIENTE_DATA', {
                clienteData: parsedData,
                enderecoEntrega: parsedData.enderecoEntrega,
                dataCapaPedido: {
                  estabelecimentoid: parsedData.estabelecimento?.estabelecimentoid,
                  clientenomeabreviado: parsedData.cliente?.clientenomeabreviado,
                  naturezaoperacaoid: parsedData.naturezaOperacao?.naturezaoperacaoid || parsedData.cliente?.naturezaoperacaoid,
                  canal_venda_id: parsedData.cliente?.canal_venda_id,
                  clienteisentoie: parsedData.cliente?.clienteisentoie,
                  tabelaprecoid: parsedData.tabelaPreco?.tabelapreco_id,
                  condicaopagamentoid: parsedData.condicaoPagamento?.condicaopagamentoid,
                },
              });

              validation = this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO'];

              if (validation) {
                this.$store.commit('InfoPedido/SET_IS_CONFIRMA_PEDIDO', true);
              }
            } catch (error) {
              console.error('Erro ao carregar dados salvos:', error);
            }
          }
        }

        if (!validation) {
          console.log('ABRIR MODAL INFO PEDIDO');
          try { if (Swal.isVisible()) Swal.close(); } catch (e) {}
          this.$bvModal.show('modal-center-info-pedido');
        } else {
          console.log('ABRIR MODAL EMBALAGENS');
          this.$bvModal.hide('modal-center-info-pedido');
          await this.hydrateSelectedItemImages();
          const itemSelected = this.$store.getters['InfoPedido/GET_ITEM_SELECTED'] || {};
          const itemId = itemSelected.item_id || itemSelected.itemId
            || itemSelected.CodItem || itemSelected.Coditem;

          // Home rows are now exact SKUs.  Do not make the user choose a
          // commercial-family packaging before opening one of these rows:
          // ModalEmbalagens resolves the exact item through checkGrade and
          // tolerates a response without packaging/grade data.
          if (itemId !== null && itemId !== undefined && String(itemId).trim() !== '') {
            this.$store.commit('slsEmbalagem/setEsEmbalg', []);
            this.$bvModal.show('modal-top-selected-embalagens');
          } else {
            // Preserve the legacy family flow for old rows that genuinely do
            // not carry a SKU, without turning the family id into a SKU.
            await infoPedidoService.getEmbalagemByFamComercialId();
            if (this.$store.getters['slsEmbalagem/getEsEmbalg'].length === 0) {
              await Swal.fire({
                icon: 'error',
                title: 'Embalagens',
                text: 'Não há embalagens disponíveis para o produto! Entre em contato com ADM Vendas.',
              });
            } else {
              this.$bvModal.show('modal-top-selected-embalagens');
            }
          }
        }
      } catch (error) {
        console.error('Erro ao processar o clique:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao validar informações',
            text: 'Verifique as Configurações do Usuário.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
      } finally {
        try { clearTimeout(loadingTimer); } catch (e) {}
        Swal.close();
      }
    },

    async hydrateSelectedItemImages() {
      const item = this.$store.getters['InfoPedido/GET_ITEM_SELECTED'];
      // The Home response already contains the images for an exact SKU.  A
      // family-image lookup would lose that identity and is unnecessary.
      if (Array.isArray(item?.imagens) && item.imagens.length) return;
      if (!item?.cod_marca || !item?.familia_comercial_id) return;

      try {
        const response = await slsMarcaService.listarImagensPorMarcaRaw(item.cod_marca, {
          familia_comercial_id: item.familia_comercial_id,
        });

        const data = Array.isArray(response) ? response : response?.data || [];
        const fullItem = data[0];

        if (fullItem?.imagens?.length) {
          this.$store.commit('InfoPedido/SET_ITEM_SELECTED', {
            ...item,
            imagens: fullItem.imagens,
          });
        }
      } catch (error) {
        console.error('Erro ao carregar imagens completas do item:', error);
      }
    },

    getUserId() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData'));
        return userData?.id || userData?.userData?.id || 'default';
      } catch (error) {
        console.error('Erro ao obter ID do usuário:', error);
        return 'default';
      }
    },
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout');

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    });

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val);
    });

    return {
      skinClasses,
    };
  },
  async mounted() {
    // Carrega informações do tenant (logo, cor, plano) — silencioso se falhar
    this.$store.dispatch('tenantStore/carregarTenant').catch(() => {});
    if (this.$store.getters['auth/IS_LOGGED_IN']) {
      this.$store.dispatch('preferencias/load').catch(() => {});
    }

    await prioridadePedido.selectPrioridadePedido();

    if (this.$store.getters['auth/IS_LOGGED_IN']) {
      try {
        const userId = this.getUserId();
        const savedFormData = localStorage.getItem(`infoPedidoForm_${userId}`);

        if (savedFormData) {
          const parsedData = JSON.parse(savedFormData);
          this.$store.commit('InfoPedido/SET_FORM_INFO_PEDIDO', parsedData);

          if (parsedData.cliente && parsedData.cliente.clienteid) {
            this.$store.commit('InfoPedido/SET_CLIENTE_DATA', {
              clienteData: parsedData,
              enderecoEntrega: parsedData.enderecoEntrega,
              dataCapaPedido: {
                estabelecimentoid: parsedData.estabelecimento?.estabelecimentoid,
                clientenomeabreviado: parsedData.cliente?.clientenomeabreviado,
                naturezaoperacaoid: parsedData.naturezaOperacao?.naturezaoperacaoid || parsedData.cliente?.naturezaoperacaoid,
                canal_venda_id: parsedData.cliente?.canal_venda_id,
                clienteisentoie: parsedData.cliente?.clienteisentoie,
                tabelaprecoid: parsedData.tabelaPreco?.tabelapreco_id,
                condicaopagamentoid: parsedData.condicaoPagamento?.condicaopagamentoid,
              },
            });
          }

          if (this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO']) {
            this.$store.commit('InfoPedido/SET_IS_CONFIRMA_PEDIDO', true);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados do pedido do localStorage:', error);
      }
    }

    this.$root.$on('infoPedido:checkGrade', async () => {
      await this.onClickItem();
    });
  },
  destroyed() {
    this.$root.$off('infoPedido:checkGrade');
  },
};
</script>

<style>
/* ── Overrides de cor primária via CSS variable ───────────────────────────── */
/* Botões primários */
.btn-primary,
.btn-primary.disabled,
.btn-primary:disabled {
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
}
.btn-primary:hover,
.btn-primary:focus,
.btn-primary:active,
.btn-primary.active,
.show > .btn-primary.dropdown-toggle,
.btn-primary:not(:disabled):not(.disabled):active,
.btn-primary:not(:disabled):not(.disabled).active {
  background-color: var(--primary-dark) !important;
  border-color: var(--primary-dark) !important;
}
.btn-outline-primary {
  color: var(--primary) !important;
  border-color: var(--primary) !important;
}
.btn-outline-primary:hover,
.btn-outline-primary:not(:disabled):not(.disabled):active {
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
  color: #fff !important;
}
.btn-flat-primary {
  color: var(--primary) !important;
}
.btn-flat-primary:hover,
.btn-flat-primary:focus {
  background-color: rgba(var(--primary-rgb, 25, 118, 210), 0.12) !important;
  color: var(--primary) !important;
}
.btn-relief-primary {
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
}

/* Badges */
.badge-primary {
  background-color: var(--primary) !important;
}
.badge-light-primary {
  background-color: rgba(var(--primary-rgb, 25, 118, 210), 0.12) !important;
  color: var(--primary) !important;
}

/* Background e texto */
.bg-primary {
  background-color: var(--primary) !important;
}
.text-primary {
  color: var(--primary) !important;
}
.border-primary {
  border-color: var(--primary) !important;
}

/* Cabeçalho de tabela */
.table:not(.table-dark) thead th,
.table:not(.table-dark) tfoot th {
  background-color: var(--primary) !important;
  color: #fff !important;
}

/* Paginação */
.pagination .page-item.active .page-link {
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
}
.pagination .page-link:not(.disabled) {
  color: var(--primary) !important;
}

/* Inputs focus */
.form-control:focus,
.custom-select:focus {
  border-color: var(--primary) !important;
  box-shadow: 0 0 0 0.2rem rgba(var(--primary-rgb, 25, 118, 210), 0.25) !important;
}

/* Checkbox/radio customizados */
.custom-control-input:checked ~ .custom-control-label::before {
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
}

/* Nav pills */
.nav-pills .nav-link.active,
.nav-pills .show > .nav-link {
  background-color: var(--primary) !important;
}

/* Progress bar */
.progress-bar {
  background-color: var(--primary) !important;
}

/* ── Sidebar ── ícones e textos ────────────────────────────────────── */
.main-menu.menu-dark .navigation li a svg,
.main-menu.menu-dark .navigation li a i,
.main-menu.menu-dark .navigation li a .feather {
  color: var(--sidebar-text) !important;
  fill: var(--sidebar-text) !important;
  stroke: var(--sidebar-text) !important;
}

/* ── Sidebar ── item ativo (qualquer nível) ─────────────────────────── */
.main-menu .navigation li.active > a,
.main-menu .navigation li.nav-item.active > a,
.main-menu .navigation .active > a {
  background: var(--sidebar-active-bg) !important;
  color: var(--sidebar-active-text, #fff) !important;
}
.main-menu .navigation li.active > a svg,
.main-menu .navigation li.active > a i,
.main-menu .navigation .active > a svg,
.main-menu .navigation .active > a i {
  color: var(--sidebar-active-text, #fff) !important;
  fill: var(--sidebar-active-text, #fff) !important;
  stroke: var(--sidebar-active-text, #fff) !important;
}

/* ── Sidebar ── grupo ativo ─────────────────────────────────────────── */
.main-menu .navigation li.sidebar-group-active > a,
.main-menu .navigation .sidebar-group-active > a {
  background: var(--sidebar-active-bg) !important;
  color: var(--sidebar-active-text, #fff) !important;
}
.main-menu .navigation li.sidebar-group-active > a svg,
.main-menu .navigation li.sidebar-group-active > a i {
  color: var(--sidebar-active-text, #fff) !important;
  fill: var(--sidebar-active-text, #fff) !important;
  stroke: var(--sidebar-active-text, #fff) !important;
}

/* ── Sidebar ── hover ───────────────────────────────────────────────── */
.main-menu.menu-dark .navigation li > a:hover {
  background: var(--sidebar-hover-bg) !important;
}

/* ── Sidebar ── cabeçalhos de seção ────────────────────────────────── */
.main-menu .navigation .navigation-header span {
  color: var(--sidebar-text) !important;
  opacity: 0.7;
}
</style>
